/**
 * Read-after-write consistency validation.
 * Verifies that a written entity can be fetched by the same query path used by the UI.
 */

import { getDebugFlags } from "./debugFlags";
import { logFlowDiagnostic } from "./flowDiagnostics";
import { classifySupabaseError } from "./errorClassifier";

export interface ValidateWriteReadOptions<T> {
  flow: string;
  entity: string;
  entityId: string;
  userId?: string;
  writeSucceeded: boolean;
  expectedPresence: boolean;
  fetchValidator: () => Promise<T | null | T[]>;
  /** Check that the result contains the entity (for lists) or is the entity (for single) */
  checkPresence?: (result: T | null | T[]) => boolean;
}

export async function validateWriteReadConsistency<T>(options: ValidateWriteReadOptions<T>): Promise<{
  consistent: boolean;
  fetchResult?: T | null | T[];
}> {
  const flags = getDebugFlags();
  if (!flags.validateReadAfterWrite || !options.writeSucceeded) {
    return { consistent: true };
  }

  try {
    const result = await options.fetchValidator();
    const present = options.checkPresence
      ? options.checkPresence(result)
      : result != null && (Array.isArray(result) ? result.length > 0 : true);

    const consistent = options.expectedPresence ? present : !present;

    if (!consistent) {
      logFlowDiagnostic({
        flow: options.flow,
        operation: "select",
        entity: options.entity,
        userId: options.userId,
        entityId: options.entityId,
        success: false,
        fetchAfterWrite: "failed",
      });
      if (flags.flowDiagnostics) {
        console.warn(
          `[StudyDo consistency] ${options.flow}: Write succeeded but read-back ${options.expectedPresence ? "did not return" : "still returns"} the entity. Likely RLS/read-model mismatch.`,
          { entityId: options.entityId, result }
        );
      }
    }

    return { consistent, fetchResult: result };
  } catch (err) {
    const classified = classifySupabaseError(err);
    logFlowDiagnostic({
      flow: options.flow,
      operation: "select",
      entity: options.entity,
      userId: options.userId,
      entityId: options.entityId,
      success: false,
      error: err,
      classified,
      fetchAfterWrite: "failed",
    });
    if (flags.flowDiagnostics) {
      console.warn(
        `[StudyDo consistency] ${options.flow}: Write succeeded but read-back threw.`,
        classified.hint,
        err
      );
    }
    return { consistent: false };
  }
}
